import React from "react";
import { motion } from "framer-motion";
import { Instagram, Music2 } from "lucide-react";
import { SOCIAL_POSTS, SOCIAL_PROFILES, toEmbed } from "../data/social";

/**
 * SocialWall — embedded Instagram/TikTok posts between Press (07) and FAQ.
 * Renders nothing until SOCIAL_POSTS has at least one entry.
 */
const SocialWall = () => {
  const posts = SOCIAL_POSTS.map((p) => ({ ...p, embed: toEmbed(p) })).filter((p) => p.embed);

  return (
    <section
      id="social"
      data-testid="social-section"
      className="relative py-32 md:py-48 bg-[#14100D] text-[#EFE7D6] overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-8 md:px-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="mb-16 md:mb-24"
        >
          <div className="flex items-center gap-6 mb-10">
            <span className="counter">08 — Social</span>
            <span className="w-16 hairline-strong h-px inline-block" />
            <span className="overline">@pogspirits</span>
          </div>
          <div className="flex flex-wrap items-end justify-between gap-10">
            <h2 className="display-tight text-[3rem] sm:text-6xl md:text-8xl max-w-4xl">
              Follow the
              <br />
              <span className="italic font-light text-[#DCCFB4]">golden hour.</span>
            </h2>
            <div className="flex gap-4 pb-4">
              <a
                href={SOCIAL_PROFILES.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-follow-instagram"
                className="flex items-center gap-2 px-6 py-3 border border-[#EFE7D6]/25 text-[11px] tracking-[0.25em] uppercase hover:border-[#F26B1F] hover:text-[#F26B1F] transition-colors duration-300"
              >
                <Instagram size={16} /> Instagram
              </a>
              <a
                href={SOCIAL_PROFILES.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-follow-tiktok"
                className="flex items-center gap-2 px-6 py-3 border border-[#EFE7D6]/25 text-[11px] tracking-[0.25em] uppercase hover:border-[#F26B1F] hover:text-[#F26B1F] transition-colors duration-300"
              >
                <Music2 size={16} /> TikTok
              </a>
            </div>
          </div>
        </motion.div>

        {posts.length === 0 && (
          <div className="grid sm:grid-cols-2 gap-8 md:gap-10" data-testid="social-empty-state">
            {[
              { name: "Instagram", handle: "@pogspirits", url: SOCIAL_PROFILES.instagram, Icon: Instagram, note: "Bottle drops, pours & island life" },
              { name: "TikTok", handle: "@pogspirits", url: SOCIAL_PROFILES.tiktok, Icon: Music2, note: "Behind the pour, in motion" },
            ].map(({ name, handle, url, Icon, note }) => (
              <motion.a
                key={name}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="group relative overflow-hidden border border-[#EFE7D6]/10 p-10 md:p-14 hover:border-[#F26B1F]/60 transition-colors duration-300"
                data-testid={`social-tile-${name.toLowerCase()}`}
              >
                <div className="absolute inset-0 opacity-10 sunburst-ghost pointer-events-none" />
                <Icon size={32} className="text-[#F26B1F] mb-8" />
                <div className="font-display text-3xl md:text-4xl mb-2 group-hover:text-[#F26B1F] transition-colors duration-300">
                  {handle}
                </div>
                <div className="overline text-[10px] text-[#EFE7D6]/50 mb-8">{name} · {note}</div>
                <span className="text-[11px] tracking-[0.25em] uppercase text-[#EFE7D6]/70 group-hover:text-[#F26B1F] transition-colors duration-300">
                  Follow along →
                </span>
              </motion.a>
            ))}
          </div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {posts.map((p, i) => (
            <motion.figure
              key={p.url}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="group"
              data-testid={`social-post-${i}`}
            >
              <div className="relative overflow-hidden border border-[#EFE7D6]/10 bg-[#0f0a07] aspect-[9/16] max-h-[640px]">
                <iframe
                  src={p.embed.src}
                  title={p.caption || `POG Spirits ${p.embed.type} post ${i + 1}`}
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  scrolling="no"
                  allow="encrypted-media; picture-in-picture"
                  loading="lazy"
                  data-testid={`social-embed-${i}`}
                />
              </div>
              {p.caption && (
                <figcaption className="overline text-[10px] text-[#EFE7D6]/50 mt-4">{p.caption}</figcaption>
              )}
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SocialWall;
